/* eslint-disable @typescript-eslint/no-unused-vars */
import axios from "axios";
import React, { useState, useMemo, useCallback } from "react";
import SweetAlert2 from "react-sweetalert2";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from "@chakra-ui/react";

interface Role {
  role: string;
  merchantId: string;
  subaccountId: string;
}
interface DeleteRoleModalProps {
  setSwalProps: any;
  username: string;
  email: string;
  merchantName: string;
  subaccountname: string;
  isOpen: boolean;
  onClose: () => void;
  selectedRole: any;
  setUserRoles: any;
  editUserChannel: BroadcastChannel;
  fetchUserRoles: any;
  setIsDeleting: any;
  userRoles: Role[];
}

const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
  setSwalProps,
  username,
  email,
  merchantName,
  subaccountname,
  isOpen,
  onClose,
  selectedRole,
  setUserRoles,
  editUserChannel,
  fetchUserRoles,
  setIsDeleting,
  userRoles
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const sysUsername = localStorage.getItem("username")?.toLowerCase();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    setIsSubmitting(true);
    setIsDeleting(true);
    const havingRole: Role[] = userRoles.filter(roleObj => roleObj.role === selectedRole.role);
    const havingRoleCountAfterdeleted: number = havingRole.length - 1;

    const selectedRoles: Role[] = userRoles.filter(roleObj => roleObj.role === selectedRole.role && roleObj.merchantId === selectedRole.merchantId && roleObj.subaccountId === selectedRole.subaccountId);
    const selectedRolesCount: number = selectedRoles.length;
    if (selectedRolesCount === 1) {
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/${userRole}/user/action/delete-user-role`,
          {
            email: email,
            username: username,
            role: selectedRole?.role,
            merchantId: selectedRole?.merchantId,
            subaccountId: selectedRole?.subaccountId,
            userRole,
            sysUsername,
            haveLeft: havingRoleCountAfterdeleted,
          }
        );

        // Close modal first, then show success message
        setIsSubmitting(false);
        onClose();

        setSwalProps({
          show: true,
          icon: "success",
          title: "Success",
          html: "User's role has been remove",
          showConfirmButton: true,
          didClose: () => {
            setIsDeleting(false);
            setSwalProps({});
            fetchUserRoles(true);
          },
        });
      } catch (error) {
        setIsSubmitting(false);
        onClose();

        console.error("Error deleting user role:", error);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Failed to remove role. Please try again.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsDeleting(false);
          },
        });
      }
    } else {
      setIsSubmitting(false);
      onClose();

      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Please select row to remove",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsDeleting(false);
        },
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
        <ModalOverlay className="bg-[#000] !opacity-30" />
        <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
          <ModalBody className="p-6 md:p-7">
            <div className="mb-4 flex items-start gap-3">
              <div className="rounded-xl bg-red-50 p-2 text-red-600 ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/30">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Remove User Role
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Username:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{email}</span>
                </div>
              </div>
            </div>

            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
              You're about to remove the user role as <span className="font-semibold">{selectedRole?.role}</span> from merchant <span className="font-semibold">{merchantName}</span>{selectedRole?.role !== "Merchant" && subaccountname && <> and subaccount <span className="font-semibold">{subaccountname}</span></>}.
              <br /><br />
              Please confirm if you wish to proceed.
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={onClose}
                isDisabled={isSubmitting}
                className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
              >
                Cancel
              </Button>

              <Button
                onClick={handleDelete}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                loadingText="Processing..."
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Remove user role
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DeleteRoleModal;